/**
 * Seamless wrapping for auto-rotating resource-list carousels.
 *
 * `snippets/resource-list-carousel.liquid` repeats the slide set when autoplay is on, so
 * slide `k` and slide `k + loopCount` render the same product. Once the carousel advances
 * to `loopCount` it is showing exactly what it showed at index 0, so jumping back
 * `loopCount` places with the animation disabled is invisible -- and the row can keep
 * advancing forward forever without the long scroll back that `infinite` produces when
 * more than one slide is visible.
 *
 * This deliberately leaves assets/slideshow.js alone. It listens for the slideshow's own
 * bubbling select event and acts only on elements carrying `data-loop-count`, so every
 * other carousel in the theme is untouched.
 */

import { SlideshowSelectEvent } from '@theme/events';

/** Longest we will wait for a scroll to settle before rewinding anyway. */
const SETTLE_TIMEOUT_MS = 1000;

/**
 * Reads the repeat length off the slideshow that fired the event.
 * @param {EventTarget | null} target
 * @returns {number | undefined}
 */
function loopCountFor(target) {
  if (!(target instanceof HTMLElement)) return undefined;

  const raw = target.getAttribute('data-loop-count');
  if (raw === null) return undefined;

  const count = Number.parseInt(raw, 10);
  // Zero or negative would make the rewind a no-op or send it backwards forever.
  return Number.isNaN(count) || count < 1 ? undefined : count;
}

/**
 * Resolves once the carousel has stopped moving.
 *
 * The select event is dispatched as soon as the scroll is *started*, not when it
 * finishes, so rewinding on the event itself would interrupt the animation half way and
 * snap the row -- the exact visible jump this file exists to avoid. Waiting for the
 * scroller to come to rest means the instant rewind happens at a moment when the viewport
 * already shows what it will show afterwards.
 *
 * @param {HTMLElement} slideshow
 * @returns {Promise<void>}
 */
function whenSettled(slideshow) {
  const scroller = slideshow.querySelector('slideshow-slides');
  if (!scroller) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      scroller.removeEventListener('scrollend', finish);
      resolve();
    };

    scroller.addEventListener('scrollend', finish, { once: true });

    // Safety net: browsers without scrollend, and the case where the scroll never moves
    // because the row is already where it was asked to go.
    const timer = setTimeout(finish, SETTLE_TIMEOUT_MS);
  });
}

document.addEventListener(SlideshowSelectEvent.eventName, async (event) => {
  const { target } = event;
  const loopCount = loopCountFor(target);
  if (loopCount === undefined) return;

  const index = /** @type {{detail?: {index?: number}}} */ (event).detail?.index;
  if (typeof index !== 'number' || index < loopCount) return;

  await whenSettled(/** @type {HTMLElement} */ (target));

  // The rewind lands below loopCount, so the event it fires in turn fails the test above
  // and this cannot recurse.
  /** @type {{select?: Function}} */ (target).select?.(index - loopCount, undefined, { animate: false });
});
